/** @graphql agg **/
import gql from 'graphql-tag';

import { useQueryInterop as useQuery } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { useIsLicensed, useSessionData } from '@confluence/session-data';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';

import { type TeamType, TEMPLATES_FOR_TEAM_TYPE } from '../utils/templateDetails';

import type {
	useTeamTypeUserQuery,
	useTeamTypeUserQueryVariables,
} from './__types__/useTeamTypeUserQuery';

const teamTypeQuery = gql`
	query useTeamTypeUserQuery($userId: ID!) {
		user(accountId: $userId) {
			__typename
			... on AtlassianAccountUser {
				extendedProfile {
					teamType
				}
			}
		}
	}
`;

const TEAM_TYPES = Object.keys(TEMPLATES_FOR_TEAM_TYPE);

const sanitizeTeamType = (teamType: string | undefined | null): TeamType | undefined => {
	if (teamType && TEAM_TYPES.includes(teamType)) {
		return teamType as TeamType;
	}
	return undefined;
};

export const useTeamType = ({ skip }: { skip?: boolean }) => {
	const { userId } = useSessionData();
	const isLicensed = useIsLicensed();
	const { data, loading, error } = useQuery<useTeamTypeUserQuery, useTeamTypeUserQueryVariables>(
		teamTypeQuery,
		{
			variables: { userId: userId ?? '' },
			skip: skip || !isLicensed || !userId,
			client: getAGGClient(),
		},
	);

	if (loading) {
		return { teamType: undefined, loading: true };
	}

	if (error) {
		return { teamType: undefined, loading: false, error };
	}

	if (data?.user?.__typename === 'AtlassianAccountUser') {
		return { teamType: sanitizeTeamType(data.user.extendedProfile?.teamType), loading: false };
	}

	return { teamType: undefined, loading: false };
};
